// frontend/src/components/responsive-fixes/useResponsiveLayout.ts
//
// Single source of truth for "what viewport class is the user on?"
//
// The Majestic 2027 / Celestial OS principal stylesheets stay untouched
// per the Phase-2 dispatch (msg ~2149) — this hook is the only mechanism
// React components use to drive responsive behavior. It listens to
// matchMedia at the same breakpoints majestic.css / layout.css already
// declare so JS-driven and CSS-driven decisions agree.
//
// Breakpoints (matching layout.css and majestic.css):
//   below sm  : <  600 px   (small phone)
//   sm        : 600..767 px (large phone / phablet)
//   md        : 768..1023 px (tablet portrait, sidebar still hidden by CSS)
//   lg        : >= 1024 px  (desktop, sidebar shows + bento grids stop collapsing)
//
// For Layout.tsx the load-bearing question is "is the sidebar visible?"
// which is answered by `sidebarVisible` (true at >= 900 px to match the
// existing `.afaq-sidebar { display: none }` rule in layout.css:561).

import { useEffect, useState } from "react";

export interface ResponsiveLayout {
  /** True when viewport >= 1024 px (lg+). */
  readonly isDesktop: boolean;
  /** True when viewport in 768..1023 px. */
  readonly isTablet: boolean;
  /** True when viewport in 600..767 px. */
  readonly isPhablet: boolean;
  /** True when viewport < 600 px. */
  readonly isPhone: boolean;
  /**
   * True when the principal stylesheet renders the desktop sidebar
   * (`.afaq-sidebar` is `display: none` below 900 px per layout.css:561).
   * Layout.tsx uses this to decide whether to apply the 80/280 marginLeft.
   */
  readonly sidebarVisible: boolean;
  /** Raw window.innerWidth at last evaluation. */
  readonly width: number;
}

const SSR_DEFAULT: ResponsiveLayout = {
  isDesktop: true,
  isTablet: false,
  isPhablet: false,
  isPhone: false,
  sidebarVisible: true,
  width: 1280,
};

function evaluate(): ResponsiveLayout {
  if (typeof window === "undefined") return SSR_DEFAULT;
  const w = window.innerWidth;
  const isDesktop = w >= 1024;
  const isTablet = w >= 768 && w < 1024;
  const isPhablet = w >= 600 && w < 768;
  const isPhone = w < 600;
  const sidebarVisible = w >= 900;
  return { isDesktop, isTablet, isPhablet, isPhone, sidebarVisible, width: w };
}

export function useResponsiveLayout(): ResponsiveLayout {
  const [state, setState] = useState<ResponsiveLayout>(() => evaluate());

  useEffect(() => {
    if (typeof window === "undefined") return;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setState(evaluate()));
    };
    window.addEventListener("resize", onResize, { passive: true });
    window.addEventListener("orientationchange", onResize);
    onResize();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  return state;
}
